import {
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';

import {
  DocumentDownloadOutline,
  CloseCircleOutline,
  bulkImportFiles,
  useDownload,
  ExclamationHexagonBold,
} from '@aslanpay/portal-common-lib';
import appEnv from 'app/config/appEnv';

const S3_PUBLIC_BUCKET_URL = appEnv.VITE_S3_BUCKET_URI;

interface InstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function InstructionsModal({ isOpen, onClose }: InstructionsModalProps) {
  const download = useDownload();

  const handleDownloadTemplate = () => {
    const fileName = 'Import Employees.csv';

    const fileUrl = bulkImportFiles.IMPORT_GIFTING_EMPLOYEES;
    const templateUrl = `${S3_PUBLIC_BUCKET_URL}${fileUrl}`;

    download(templateUrl, fileName);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent minW="800px" p="24px">
        <ModalHeader>
          <Flex align="center" justify="space-between">
            <Text textStyle="h6SemiBold">Template instructions</Text>
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close add people modal"
              onClick={onClose}
            />
          </Flex>
        </ModalHeader>
        <ModalBody>
          <VStack align="stretch" spacing="30px">
            <Text textStyle="body" color="brand.mutedPurple">
              Before uploading your file, please download the template we provide and follow the
              instructions below.
            </Text>

            <VStack align="stretch" spacing="10px">
              <Text textStyle="h6SemiBold" color="brand.mutedPurple">
                Template columns
              </Text>

              <Flex
                bgColor="brand.lightGray2"
                border="1px solid"
                borderRadius="5px"
                borderColor="brand.lightGray1"
                flexDir="column"
                gap="10px"
                px="20px"
                py="12px"
              >
                <Flex
                  justify="space-between"
                  align="center"
                  py="8px"
                  borderBottom="1px solid"
                  borderColor="brand.lightGray1"
                >
                  <Text textStyle="h6SemiBold">First Name (mandatory):</Text>
                  <Text textStyle="body" color="brand.mutedPurple">
                    Team member&apos;s first name. Max 100 characters
                  </Text>
                </Flex>

                <Flex
                  justify="space-between"
                  align="center"
                  py="8px"
                  borderBottom="1px solid"
                  borderColor="brand.lightGray1"
                >
                  <Text textStyle="h6SemiBold">Last Name (mandatory):</Text>
                  <Text textStyle="body" color="brand.mutedPurple">
                    Team member&apos;s last name. Max 100 characters
                  </Text>
                </Flex>

                <Flex
                  justify="space-between"
                  align="center"
                  py="8px"
                  borderBottom="1px solid"
                  borderColor="brand.lightGray1"
                >
                  <Text textStyle="h6SemiBold">Company email (mandatory):</Text>
                  <Text textStyle="body" color="brand.mutedPurple">
                    Email used to sign up and receive an invitation
                  </Text>
                </Flex>

                <Flex
                  justify="space-between"
                  align="center"
                  py="8px"
                  borderBottom="1px solid"
                  borderColor="brand.lightGray1"
                >
                  <Text textStyle="h6SemiBold">Start date:</Text>
                  <Text textStyle="body" color="brand.mutedPurple">
                    Date they joined the company. Format: DD/MM/YYYY
                  </Text>
                </Flex>

                <Flex justify="space-between" align="center" py="8px">
                  <Text textStyle="h6SemiBold">Leave date (optional):</Text>
                  <Text textStyle="body" color="brand.mutedPurple">
                    Date they left or will leave Format: DD/MM/YYYY
                  </Text>
                </Flex>
              </Flex>
            </VStack>

            <Flex direction="column" gap="10px" bg="brand.yellowAlpha10" borderRadius="5px">
              <Flex flexDir="row" align="center" gap="10px">
                <ExclamationHexagonBold boxSize="24px" color="system.warning" />
                <Text textStyle="h6SemiBold" color="brand.mutedPurple">
                  Important notes
                </Text>
              </Flex>
              <VStack align="stretch" spacing="10px" pl="10px">
                <Text textStyle="body" color="brand.mutedPurple">
                  • Do not rename or remove any columns.
                </Text>
                <Text textStyle="body" color="brand.mutedPurple">
                  • Keep the header row exactly as provided.
                </Text>
                <Text textStyle="body" color="brand.mutedPurple">
                  • Save your file as CSV (.csv) before uploading.
                </Text>
              </VStack>
            </Flex>
          </VStack>
        </ModalBody>

        <ModalFooter gap="12px" mt="30px">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<DocumentDownloadOutline boxSize="20px" />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button size="sm" onClick={onClose}>
            Got it
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default InstructionsModal;
